import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, 
  Building2, 
  Eye, 
  Calendar,
  AlertTriangle,
  Clock,
  TrendingUp,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { api } from '../../utils/api';

const Due = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overallDue, setOverallDue] = useState({
    driverDue: 0,
    vendorDue: 0,
    totalDue: 0
  });
  const [regionalOffices, setRegionalOffices] = useState([]);

  useEffect(() => {
    fetchOverallDue();
    fetchRegionalOffices();
  }, []);

  const fetchOverallDue = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payments/due/overall');
      setOverallDue(response.data);
    } catch (error) {
      console.error('Error fetching overall due:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegionalOffices = async () => {
    try {
      const response = await api.get('/payments/due/regional-offices');
      setRegionalOffices(response.data);
    } catch (error) {
      console.error('Error fetching regional offices:', error);
    }
  };


  const getDueStatus = (nextDueDate) => {
    const now = Date.now();
    const dueDate = new Date(nextDueDate).getTime();
    const diffDays = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    
    if (dueDate < now) {
      return { status: 'overdue', text: 'Overdue', color: 'text-red-600 bg-red-100' };
    } else if (diffDays === 0) {
      return { status: 'today', text: 'Due Today', color: 'text-orange-600 bg-orange-100' };
    } else {
      return { status: 'pending', text: `Due in ${diffDays} days`, color: 'text-blue-600 bg-blue-100' };
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && !overallDue.totalDue) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Due Payments</h1>
        <p className="text-gray-600">Manage and track pending payments to drivers and vendors</p>
      </div>

      {/* Overall Due Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Driver Due</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(overallDue.driverDue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Building2 className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Vendor Due</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(overallDue.vendorDue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <span className="text-2xl font-bold text-red-600">₹</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Due</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(overallDue.totalDue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Regional Offices Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Due by Regional Office</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Regional Office
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Driver Due
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor Due
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Due
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {regionalOffices.map((office) => (
                <tr key={office._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{office.name}</div>
                    <div className="text-sm text-gray-500">{office.address}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(office.driverDue || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(office.vendorDue || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency((office.driverDue || 0) + (office.vendorDue || 0))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => navigate(`/payments/due/${office._id}`)}
                      className="text-indigo-600 hover:text-indigo-900 flex items-center"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default Due;

